import { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { ArrowUpRight, Mail, MapPin, CalendarClock, Phone } from "lucide-react";
import ScrollReveal from "../components/ScrollReveal";
import { CALENDLY_URL, SERVICES, CONTACT_INFO } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll be in touch within 24 hours.");
      setForm({
        name: "",
        email: "",
        company: "",
        service: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or book a call directly.");
    }
    setLoading(false);
  };

  return (
    <div data-testid="contact-page">
      <Toaster position="top-center" theme="dark" />
      <section className="pt-36 md:pt-44 pb-16 md:pb-24 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <ScrollReveal>
            <div className="text-xs uppercase tracking-[0.4em] text-white/40 mb-4">
              / Contact
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.95]">
              Let's forge<br /><span className="text-white/40">something sharp.</span>
            </h1>
            <p className="mt-6 text-white/60 max-w-2xl text-base md:text-lg leading-relaxed">
              Drop us a note or book a 30-min Zoom call. We respond within 24
              hours, and usually faster.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-5 gap-10 lg:gap-16">
          {/* Info */}
          <ScrollReveal className="lg:col-span-2 space-y-8">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">
                / Fastest route
              </div>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-book-call"
                className="group block border border-white/15 p-6 hover:bg-violet-500/10 hover:border-violet-400/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-xl md:text-2xl font-extrabold uppercase tracking-tight flex items-center gap-3">
                      <CalendarClock size={20} />
                      Book a Zoom Call
                    </div>
                    <div className="text-xs uppercase tracking-[0.25em] text-white/50 group-hover:text-black/60 mt-2">
                      30 min · Free consultation
                    </div>
                  </div>
                  <ArrowUpRight size={20} />
                </div>
              </a>
            </div>

            <div className="space-y-5">
              <div className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">
                / Or email us
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <Mail size={16} />
                <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-violet-300 transition-colors" data-testid="contact-email-link">
                  {CONTACT_INFO.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <Phone size={16} />
                <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`} className="hover:text-violet-300 transition-colors" data-testid="contact-phone-link">
                  {CONTACT_INFO.phone}
                </a>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <MapPin size={16} />
                Mumbai · Bengaluru · Remote
              </div>
            </div>
          </ScrollReveal>

          {/* Form */}
          <ScrollReveal delay={0.1} className="lg:col-span-3">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="border border-white/10 bg-[#080808] p-6 md:p-10 space-y-5"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field
                  label="Name *"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  testId="contact-name"
                />
                <Field
                  label="Email *"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  testId="contact-email-input"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <Field
                  label="Company"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  testId="contact-company"
                />
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                    Service
                  </label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={onChange}
                    data-testid="contact-service"
                    className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white transition-colors"
                  >
                    <option value="" className="bg-black">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.title} className="bg-black">
                        {s.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                  Budget
                </label>
                <select
                  name="budget"
                  value={form.budget}
                  onChange={onChange}
                  data-testid="contact-budget"
                  className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white transition-colors"
                >
                  <option value="" className="bg-black">Select a range…</option>
                  <option value="< ₹25,000" className="bg-black">&lt; ₹25,000</option>
                  <option value="₹25,000 - ₹75,000" className="bg-black">₹25,000 – ₹75,000</option>
                  <option value="₹75,000 - ₹2,00,000" className="bg-black">₹75,000 – ₹2,00,000</option>
                  <option value="₹2,00,000+" className="bg-black">₹2,00,000+</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                  Tell us about the project *
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={5}
                  data-testid="contact-message"
                  className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white transition-colors resize-none"
                  placeholder="Goals, timelines, links, references…"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="w-full md:w-auto inline-flex items-center justify-center gap-3 btn-violet px-7 py-4 text-xs uppercase tracking-[0.3em] font-semibold disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send Message"}
                <ArrowUpRight size={14} />
              </button>
            </form>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, name, value, onChange, type = "text", testId }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white transition-colors"
    />
  </div>
);
